import {
  ToolbarPopoverButton,
  ToolbarPopoverSwitchRow,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { getSlideKeyBySlug } from "@-/dev.improve.haniverse.assets.slides";
import { useToggleSlideFavorite } from "@-/dev.improve.haniverse.lib.slide.favorite";
import { useFavoritedSlideKeysQueryOptions } from "@-/dev.improve.haniverse.lib.slide.read";
import { SlideLessonPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation, useQuery } from "@-/com.npmjs.tanstack__react-query";

const FAVORITE_ICON_COLOR = "#eab308";

/**
 * Favorite pill for the slide lesson toolbar. Favoriting a slide files it under the
 * Favorite Slides settings page so the user can come back to it later.
 *
 * Like every tray pill it opens a popover; the favorite state is the popover's switch. It reads
 * the current favorited set via `useFavoritedSlideKeysQueryOptions` (which stays live through
 * the `user_slide` table change-version) and flips state via `useToggleSlideFavorite`. The
 * optimistic-feeling fill comes straight from the query re-running after the write, so no local
 * state is needed.
 *
 * The favorites read is progressive (not prefetched with the slide): until it resolves the switch
 * is disabled and says so, rather than presenting "not favorited" as fact.
 */
export function SlideFavoriteButton() {
  const [slideSlug] = useContextSelectors(SlideLessonPageContext, (c) => c.slideSlug);
  const toggleSlideFavorite = useToggleSlideFavorite();
  const slideKey = getSlideKeyBySlug(slideSlug);

  const favoritedKeysQuery = useQuery(useFavoritedSlideKeysQueryOptions());
  const isFavorited =
    slideKey !== undefined &&
    favoritedKeysQuery.isSuccess &&
    favoritedKeysQuery.data.includes(slideKey);

  const toggleFavoriteMutation = useMutation({
    mutationFn: () => {
      if (slideKey === undefined) {
        throw new Error(`No slide key registered for slug "${slideSlug}".`);
      }
      return toggleSlideFavorite(slideKey);
    },
    mutationKey: ["slideFavoriteButtonToggle"],
  });

  return (
    <ToolbarPopoverButton
      accessibilityLabel={isFavorited ? "Remove slide from Favorites" : "Add slide to Favorites"}
      iconColor={isFavorited ? FAVORITE_ICON_COLOR : undefined}
      iconSize={16}
      label="Favorite"
      name="FiStar"
      testID="slide-favorite-button"
    >
      <ToolbarPopoverSwitchRow
        checked={isFavorited}
        description={
          favoritedKeysQuery.isPending
            ? "Loading your favorites…"
            : favoritedKeysQuery.isError
              ? "Couldn't load your favorites."
              : "Saved under Favorite Slides in Settings."
        }
        disabled={
          slideKey === undefined ||
          !favoritedKeysQuery.isSuccess ||
          toggleFavoriteMutation.isPending
        }
        label="Favorite this slide"
        onCheckedChange={() => toggleFavoriteMutation.mutate()}
        testID="slide-favorite-switch"
      />
    </ToolbarPopoverButton>
  );
}
